import { useMemo } from "react";
import {
  Provider,
  fetchExchange,
  createClient,
  subscriptionExchange,
  mapExchange,
} from "urql";
import { authExchange } from "@urql/exchange-auth";
import { Cache, cacheExchange } from "@urql/exchange-graphcache";
// import { requestPolicyExchange } from "@urql/exchange-request-policy";
import { retryExchange } from "@urql/exchange-retry";
// import { devtoolsExchange } from "@urql/devtools";
import schema from "../../__generated__/schema.json";
import { createClient as createWSClient, SubscribePayload } from "graphql-ws";
import { getFingerprint } from "@thumbmarkjs/thumbmarkjs";
import useToken from "../TokenProvider/use-token";
import { useToast } from "../../ui/Toast/ToastContext";

const invalidateQueries = (cache: Cache, queries: string[]) => {
  cache.inspectFields("Query").forEach((field) => {
    if (queries.includes(field.fieldName)) {
      cache.invalidate("Query", field.fieldName, field.arguments);
    }
  });
};

function ApiProvider({ children }: { children: React.ReactNode }) {
  const { refreshToken, isTokenValid, isReady, tokenRef } = useToken();
  const { showToast } = useToast();

  const wsClient = useMemo(() => {
    let activeSocket: WebSocket;
    let timedOut: ReturnType<typeof setTimeout>;
    let tokenValidInterval: ReturnType<typeof setInterval>;

    function closeSocket() {
      if (activeSocket.readyState === WebSocket.OPEN) {
        activeSocket.close(4408, "Request Timeout");
      }
    }

    return createWSClient({
      url: (import.meta.env.VITE_GRAPHQL_URL as string).replace("http", "ws"),
      connectionParams: async () => {
        if (!isTokenValid()) {
          await refreshToken();
        }

        return { token: tokenRef.current.token };
      },
      shouldRetry: () => true,
      keepAlive: 10_000, // ping server every 10 seconds
      on: {
        connected: (socket) => {
          activeSocket = socket as WebSocket;

          tokenValidInterval = setInterval(() => {
            if (!isTokenValid()) {
              closeSocket();
            }
          }, 5_000);
        },
        closed: () => {
          clearInterval(tokenValidInterval);
        },
        ping: (received) => {
          if (!received)
            // sent
            timedOut = setTimeout(() => {
              closeSocket();
            }, 5_000); // wait 5 seconds for the pong and then close the connection
        },
        pong: (received) => {
          if (received) {
            // pong is received, clear connection close timeout
            clearTimeout(timedOut);
          }
        },
      },
    });
  }, [isTokenValid, refreshToken, tokenRef]);

  const client = useMemo(() => {
    return createClient({
      url: import.meta.env.VITE_GRAPHQL_URL as string,
      exchanges: [
        // ...(import.meta.env.DEV ? [devtoolsExchange] : []),
        // Invalidate local cache after 60 seconds
        // requestPolicyExchange({
        //   ttl: 60_000,
        // }),
        mapExchange({
          onError(error) {
            if (error.networkError instanceof CloseEvent) {
              return;
            }

            showToast({
              variant: "error",
              title: error.message
                .replace("[Network Error]", "")
                .replace("[Network]", "")
                .replace("[GraphQL]", "")
                .trim(),
            });
          },
        }),
        cacheExchange({
          schema,
          optimistic: {
            includeWebpageInReference(args) {
              return {
                __typename: "ScrapedWebpage",
                id: args.id,
                includedInReference: true,
              };
            },
            excludeWebpageFromReference(args) {
              return {
                __typename: "ScrapedWebpage",
                id: args.id,
                includedInReference: false,
              };
            },
            includeWebpageInInternalLinking(args) {
              return {
                __typename: "ScrapedWebpage",
                id: args.id,
                includedInInternalLinking: true,
              };
            },
            excludeWebpageFromInternalLinking(args) {
              return {
                __typename: "ScrapedWebpage",
                id: args.id,
                includedInInternalLinking: false,
                includedInPriorityLinking: false,
              };
            },
            includeWebpageInPriorityLinking(args) {
              return {
                __typename: "ScrapedWebpage",
                id: args.id,
                includedInPriorityLinking: true,
                includedInInternalLinking: true,
              };
            },
            excludeWebpageFromPriorityLinking(args) {
              return {
                __typename: "ScrapedWebpage",
                id: args.id,
                includedInPriorityLinking: false,
              };
            },
            updateArticleSection(args) {
              return {
                __typename: "ArticleSection",
                id: args.id,
                titleDisabled: args.titleDisabled,
              };
            },
          },
          updates: {
            Mutation: {
              deleteUser: (result, args, cache) => {
                cache.invalidate({
                  __typename: "User",
                  id: args.id as string,
                });
                invalidateQueries(cache, ["users"]);
              },
              inviteUsers(result, args, cache) {
                invalidateQueries(cache, ["users"]);
              },
              revokeInvitation(result, args, cache) {
                invalidateQueries(cache, ["users"]);
              },
              deletePattern(result, args, cache) {
                cache.invalidate({
                  __typename: "Pattern",
                  id: args.id as string,
                });
                invalidateQueries(cache, ["patterns", "patternCounts"]);
              },
              deletePatterns(result, args, cache) {
                invalidateQueries(cache, ["patterns", "patternCounts"]);
              },
              generatePatterns(result, args, cache) {
                invalidateQueries(cache, [
                  "billingInfo",
                  "patterns",
                  "patternCounts",
                ]);
              },
              publishPatterns(result, args, cache) {
                invalidateQueries(cache, ["patterns", "patternCounts"]);
              },
              createPattern(result, args, cache) {
                invalidateQueries(cache, ["patterns", "patternCounts"]);
              },
              deleteArticle(result, args, cache) {
                cache.invalidate({
                  __typename: "Article",
                  id: args.id as string,
                });
                invalidateQueries(cache, ["articles", "articleCounts"]);
              },
              deleteArticles(result, args, cache) {
                invalidateQueries(cache, ["articles", "articleCounts"]);
              },
              generateArticles(result, args, cache) {
                invalidateQueries(cache, [
                  "billingInfo",
                  "articles",
                  "articleCounts",
                ]);
              },
              generateArticle(result, args, cache) {
                invalidateQueries(cache, [
                  "billingInfo",
                  "articles",
                  "articleCounts",
                ]);
              },
              publishArticles(result, args, cache) {
                invalidateQueries(cache, ["articles", "articleCounts"]);
              },
              publishArticle(result, args, cache) {
                invalidateQueries(cache, ["articles", "articleCounts"]);
              },
              generateKeywordArticles(result, args, cache) {
                invalidateQueries(cache, [
                  "billingInfo",
                  "articles",
                  "articleCounts",
                ]);
              },
              cancelArticleGeneration(result, args, cache) {
                invalidateQueries(cache, [
                  "billingInfo",
                  "articles",
                  "articleCounts",
                ]);
              },
              cancelArticlesGeneration(result, args, cache) {
                invalidateQueries(cache, [
                  "billingInfo",
                  "articles",
                  "articleCounts",
                  "patterns",
                  "patternCounts",
                ]);
              },
              deleteFeature(result, args, cache) {
                cache.invalidate({
                  __typename: "Feature",
                  id: args.id as string,
                });
                invalidateQueries(cache, ["features"]);
              },
              createFeature(result, args, cache) {
                invalidateQueries(cache, ["features"]);
              },
              createFeatures(result, args, cache) {
                invalidateQueries(cache, ["features"]);
              },
              updateFeatures(result, args, cache) {
                invalidateQueries(cache, ["features"]);
              },
              deleteFeatures(result, args, cache) {
                invalidateQueries(cache, ["features"]);
              },
              createAudience(result, args, cache) {
                invalidateQueries(cache, ["audiences"]);
              },
              createAudiences(result, args, cache) {
                invalidateQueries(cache, ["audiences"]);
              },
              updateAudiences(result, args, cache) {
                invalidateQueries(cache, ["audiences"]);
              },
              deleteAudiences(result, args, cache) {
                invalidateQueries(cache, ["audiences"]);
              },
              updateWebflowConnectionSettings(result, args, cache) {
                invalidateQueries(cache, ["canPublish", "webflowConnection"]);
              },
              createGoalPages(result, args, cache) {
                invalidateQueries(cache, ["goalPages"]);
              },
              updateGoalPages(result, args, cache) {
                invalidateQueries(cache, ["goalPages"]);
              },
              deleteGoalPages(result, args, cache) {
                invalidateQueries(cache, ["goalPages"]);
              },
              setupWebflowConnection(result, args, cache) {
                invalidateQueries(cache, ["canPublish"]);
              },
              deleteWebflowConnection(result, args, cache) {
                invalidateQueries(cache, [
                  "webflowConnection",
                  "webflowIntegration",
                  "webflowCollections",
                  "webflowFields",
                  "canPublish",
                ]);
              },
              deleteWordpressConnection(result, args, cache) {
                invalidateQueries(cache, ["wordpressConnection"]);
              },
              setupShopifyConnection(result, args, cache) {
                invalidateQueries(cache, ["canPublish"]);
              },
              deleteShopifyConnection(result, args, cache) {
                invalidateQueries(cache, [
                  "shopifyConnection",
                  "shopifyBlogs",
                  "canPublish",
                ]);
              },
              setupWixConnection(result, args, cache) {
                invalidateQueries(cache, ["canPublish", "wixConnection"]);
              },
              deleteWixConnection(result, args, cache) {
                invalidateQueries(cache, ["canPublish", "wixConnection"]);
              },
              updateWebsiteSettings(result, args, cache) {
                invalidateQueries(cache, ["scrapedWebpages"]);
              },
              linkTenants(result, args, cache) {
                invalidateQueries(cache, ["billingInfo"]);
              },
              createTags(result, args, cache) {
                invalidateQueries(cache, ["tags"]);
              },
              updateTags(result, args, cache) {
                invalidateQueries(cache, ["tags"]);
              },
              deleteTags(result, args, cache) {
                invalidateQueries(cache, ["tags"]);
              },
              adminDeleteTenant(_result, args, cache) {
                if (typeof args.id === "string") {
                  cache.invalidate({
                    __typename: "AdminTenant",
                    id: args.id,
                  });
                }
              },
              getMoreKeywordRecommendations(result, args, cache) {
                invalidateQueries(cache, ["keywordRecommendations"]);
              },
            },
            Query: {
              keywords(result, args, cache) {
                invalidateQueries(cache, [
                  "billingInfo",
                  "keywordQueryUsageForCurrentPeriod",
                ]);
              },
              keywordsByUrl(result, args, cache) {
                invalidateQueries(cache, [
                  "billingInfo",
                  "keywordQueryUsageForCurrentPeriod",
                ]);
              },
              keywordGap(result, args, cache) {
                invalidateQueries(cache, [
                  "billingInfo",
                  "keywordQueryUsageForCurrentPeriod",
                ]);
              },
            },
          },
          keys: {
            KeywordPage: () => null,
            KeywordRecommendationPage: () => null,
            ArticlePage: () => null,
            PatternPage: () => null,
            CreditUsagePage: () => null,
            UserPage: () => null,
            KeywordWithUrlPage: () => null,
            ScrapedWebpagePage: () => null,
            ImagePage: () => null,
            AdminArticlePage: () => null,
            AdminTenantPage: () => null,
            WordpressConnectionSettings: () => null,
            WebflowConnectionSettings: () => null,
            WebflowFieldMapping: () => null,
            WebflowFieldMappingOption: () => null,
            BillingInfo: (data) => data.__typename,
            CanPublishResponse: (data) => data.__typename,
            BillingTierFeatures: () => null,
            TemplateNode: () => null,
            PatternSymbol: () => null,
            Usage: (data) => data.__typename,
            KeywordQueryUsage: (data) => data.__typename,
            ArticleCounts: () => null,
            ArticleStats: () => null,
            PatternCounts: (data) => data.__typename,
            ContentSettings: (data) => data.__typename,
            ImageGenerationSettings: (data) => data.__typename,
            BrandSettings: (data) => data.__typename,
            WebsiteSettings: (data) => data.__typename,
            KeywordSettings: (data) => data.__typename,
            CreditUsage: (data) => data.createdAt as string,
            Keyword: (data) => data.keyword as string,
            KeywordWithUrl: (data) => data.keyword as string,
            KeywordRecommendation: (data) => data.keyword as string,
            Trend: () => null,
          },
        }),
        authExchange(async (utils) => {
          const fingerprint = await getFingerprint();

          return {
            addAuthToOperation(operation) {
              if (!tokenRef.current.token) {
                return operation;
              }

              return utils.appendHeaders(operation, {
                Authorization: `Bearer ${tokenRef.current.token}`,
                "X-Fingerprint": fingerprint,
                "X-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
              });
            },
            willAuthError() {
              return !isTokenValid();
            },
            didAuthError(error) {
              return error.graphQLErrors.some(
                (e) => e.message === "Unauthorized",
              );
            },
            async refreshAuth() {
              await refreshToken();
            },
          };
        }),
        retryExchange({}),
        fetchExchange,
        subscriptionExchange({
          forwardSubscription(operation) {
            return {
              subscribe: (sink) => {
                const dispose = wsClient.subscribe(
                  operation as SubscribePayload,
                  sink,
                );
                return {
                  unsubscribe: dispose,
                };
              },
            };
          },
        }),
      ],
    });
  }, [isTokenValid, refreshToken, showToast, tokenRef, wsClient]);

  if (!isReady) {
    return null;
  }

  return <Provider value={client}>{children}</Provider>;
}

export default ApiProvider;
